import { httpStatusOf, isRetryable, outboxReason, type OutboxKind } from '@/domain';
import type { OutboxQueue } from '../ports/OutboxQueue';

/**
 * Section 9 rule 1: the command is written to the outbox, the UI updates
 * optimistically, then the send is attempted.
 *
 * The order matters and is the whole point. Writing first means a phone that
 * dies between the tap and the response still holds the work; sending first
 * and recording afterwards would lose exactly the case this feature exists for.
 */

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Thrown when the send failed but the command is safely queued.
 *
 * The distinction matters to the screen, not just to the log. A server that
 * refused the command is a problem the technician must see and stay with. A
 * connection that was not there is not: the work is recorded, it will be sent,
 * and holding somebody on the job screen with "Failed to fetch" would make them
 * tap it again and then stop trusting the system. Section 9 exists to prevent
 * exactly that.
 *
 * The original error is kept, so anything that wants the detail still has it.
 */
export class QueuedWhileOfflineError extends Error {
  constructor(
    readonly entryId: string,
    override readonly cause: unknown,
  ) {
    super(messageOf(cause));
    this.name = 'QueuedWhileOfflineError';
  }
}

export interface DispatchRequest<Command> {
  kind: OutboxKind;
  kitId: string;
  /** The command itself, so a replay can reissue it byte for byte. */
  command: Command;
  createdAt: string;
}

/**
 * Records the command, sends it, and marks the outcome.
 *
 * The error is rethrown so the mutation that called this keeps its existing
 * rollback and its existing toast. The outbox is a durability layer underneath
 * the optimistic UI, not a replacement for it.
 */
export async function dispatch<Command extends { requestId: string }, Result>(
  queue: OutboxQueue,
  request: DispatchRequest<Command>,
  send: (command: Command) => Promise<Result>,
): Promise<Result> {
  await queue.enqueue({
    id: request.command.requestId,
    kind: request.kind,
    kitId: request.kitId,
    payload: request.command,
    createdAt: request.createdAt,
  });

  try {
    const result = await send(request.command);
    await queue.markSent(request.command.requestId);
    return result;
  } catch (error) {
    const retryable = isRetryable(httpStatusOf(error));
    // Why, as the tray can say it: a sentence for a person, a status, or "no answer".
    await queue.markFailed(request.command.requestId, outboxReason(error), retryable);

    // Either way this throws: the mutation must not report success for work the
    // server has not accepted. What changes is which failure the screen sees.
    if (retryable) throw new QueuedWhileOfflineError(request.command.requestId, error);
    throw error;
  }
}
